/**
 * Account Aggregator Create Drawer
 */

import React, { useEffect, useState } from 'react';
import { Drawer, DrawerContent, DrawerHeader, DrawerOverlay, Button, InputField } from 'equal-ds-ui';
import type {
  AccountAggregatorCreateDrawerProps,
  RegisterAccountAggregatorPayload,
} from '../../../types/account-aggregator';

interface FormState extends RegisterAccountAggregatorPayload {}
interface FormErrors {
  name?: string;
  email?: string;
  phoneno?: string;
  contact_person?: string;
  address?: string;
  licence_no?: string;
}

const DEFAULT_FORM: FormState = {
  name: '',
  email: '',
  phoneno: '',
  contact_person: '',
  address: '',
  status: 0,
  licence_no: '',
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[0-9]{7,15}$/;

export const AccountAggregatorCreateDrawer: React.FC<AccountAggregatorCreateDrawerProps> = ({
  isOpen,
  onSubmit,
  onClose,
  isSubmitting,
}) => {
  const [formState, setFormState] = useState<FormState>(DEFAULT_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setFormState(DEFAULT_FORM);
      setErrors({});
      setSubmitError(null);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const validate = (): boolean => {
    const nextErrors: FormErrors = {};
    if (!formState.name.trim()) nextErrors.name = 'Name is required.';
    if (!formState.email.trim() || !EMAIL_REGEX.test(formState.email)) nextErrors.email = 'Enter a valid email.';
    if (!formState.phoneno.trim() || !PHONE_REGEX.test(formState.phoneno)) nextErrors.phoneno = 'Enter a valid phone number.';
    if (!formState.contact_person.trim()) nextErrors.contact_person = 'Contact person is required.';
    if (!formState.address.trim()) nextErrors.address = 'Address is required.';
    if (!formState.licence_no.trim()) nextErrors.licence_no = 'Licence number is required.';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    if (!validate()) {
      return;
    }
    try {
      await onSubmit(formState);
    } catch (error: any) {
      const message = error?.message || 'Unable to create account aggregator.';
      setSubmitError(message);
    }
  };

  const updateField = (field: keyof FormState, value: string | number) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <>
      <DrawerOverlay />
      <Drawer variant="overlay" width="640px">
        <DrawerHeader className="border-b border-border-default pb-4" onClose={onClose}>
          <div>
            <h2 className="text-xl font-semibold text-text-primary">Create account aggregator</h2>
            <p className="text-sm text-text-secondary mt-1">
              Provide details to register a new account aggregator.
            </p>
          </div>
        </DrawerHeader>
        <DrawerContent className="p-6 space-y-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputField
              label="Name"
              value={formState.name}
              onChange={(event) => updateField('name', event.target.value)}
              disabled={isSubmitting}
              error={errors.name}
            />
            <InputField
              label="Email"
              type="email"
              value={formState.email}
              onChange={(event) => updateField('email', event.target.value)}
              disabled={isSubmitting}
              error={errors.email}
            />
            <InputField
              label="Phone number"
              value={formState.phoneno}
              onChange={(event) => updateField('phoneno', event.target.value)}
              disabled={isSubmitting}
              error={errors.phoneno}
            />
            <InputField
              label="Contact person"
              value={formState.contact_person}
              onChange={(event) => updateField('contact_person', event.target.value)}
              disabled={isSubmitting}
              error={errors.contact_person}
            />
            <InputField
              label="Licence number"
              value={formState.licence_no}
              onChange={(event) => updateField('licence_no', event.target.value)}
              disabled={isSubmitting}
              error={errors.licence_no}
            />
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Status
              </label>
              <select
                value={formState.status}
                onChange={(event) => updateField('status', Number(event.target.value) || 0)}
                disabled={isSubmitting}
                className="w-full rounded-lg border border-border-default px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100"
              >
                <option value={0}>Inactive</option>
                <option value={1}>Active</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">Address</label>
            <textarea
              value={formState.address}
              onChange={(event) => updateField('address', event.target.value)}
              disabled={isSubmitting}
              className={`w-full min-h-[140px] rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                errors.address
                  ? 'border-red-300 focus:ring-red-200'
                  : 'border-border-default focus:ring-primary-100'
              }`}
              placeholder="Enter address"
            />
            {errors.address && <p className="mt-1 text-xs text-red-600">{errors.address}</p>}
          </div>

          {submitError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {submitError}
            </div>
          )}
        </DrawerContent>
        <div className="px-6 py-4 border-t border-border-default flex justify-end gap-3 bg-background-secondary flex-shrink-0">
          <Button variant="secondary" size="md" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" size="md" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create'}
          </Button>
        </div>
      </Drawer>
    </>
  );
};

